import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, CircularProgress, IconButton, Chip, TextField,
  InputAdornment, FormControl, InputLabel, Select, MenuItem, TableSortLabel, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payments';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAppSettings } from '../../contexts/AppSettingsContext';

// Custom sortable table header cell component
const SortableTableCell = ({ id, label, align, orderBy, order, onRequestSort }) => {
  const createSortHandler = () => {
    onRequestSort(id);
  };

  return (
    <TableCell 
      align={align} 
      sortDirection={orderBy === id ? order : false}
      onClick={createSortHandler}
      sx={{ cursor: 'pointer' }}
    >
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : 'asc'}
        onClick={createSortHandler}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
};

const CustomersList = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');
  const [associations, setAssociations] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    current_page: 1,
    per_page: 10
  });
  
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  
  // Get allowed statuses from context (will update automatically if context changes)
  const { allowedStatuses } = useAppSettings();
  
  // Status options (derived from allowed statuses)
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    ...(allowedStatuses.map(status => {
      // Map display value to API value (lowercase with underscores)
      const apiValue = status === 'Over Exposure' ? 'over_exposure' : 
                      status.toLowerCase().replace(/ /g, '_');
      return { value: apiValue, label: status };
    }))
  ];
  
  // State for sorting
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  // Check if user is admin, fetch associations, and initialize first load
  useEffect(() => {
    const checkUserAndFetchAssociations = async () => {
      try {
        // Get user info from storage
        const userString = localStorage.getItem('user');
        if (userString) {
          const user = JSON.parse(userString);
          const isSuperAdmin = user.role === 'superadmin';
          setIsAdmin(isSuperAdmin);
          
          // If superadmin, fetch all associations
          if (isSuperAdmin) {
            const response = await api.get('/api/admin/associations');
            const associationsData = response.data;
            setAssociations(associationsData);
            
            // Set the first association as selected by default if there are any
            if (associationsData.length > 0) {
              const firstAssociationId = associationsData[0].id;
              // Set selected association (allowed statuses managed by context)
              setSelectedAssociation(firstAssociationId);
              
              // Explicitly fetch customers with the first association
              fetchCustomers(firstAssociationId);
            }
          } else {
            // For non-admin users, fetch customers immediately
            // Also fetch their association for allowed statuses
            const userAssociationId = user.association_id;
            if (userAssociationId) {
              try {
                // Non-admin users need their association's allowed statuses too
                const associationResponse = await api.get(`/api/admin/associations/${userAssociationId}`);
                const associationData = associationResponse.data;
                // Note: allowed statuses are now managed by the app context
              } catch (associationError) {
                console.error('Error fetching user association:', associationError);
                // Continue with default allowed statuses
              }
            }
            fetchCustomers();
          }
        }
      } catch (error) {
        console.error('Error fetching associations:', error);
        toast.error('Failed to load associations. Please try again.');
      }
    };
    
    checkUserAndFetchAssociations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch customers when search/pagination/association/sort/status changes
  useEffect(() => {
    // Only execute if selectedAssociation is set (for admin) or user is not admin
    if ((isAdmin && selectedAssociation) || !isAdmin) {
      // Add a small delay to avoid making too many requests while typing
      const timeoutId = setTimeout(() => {
        fetchCustomers();
      }, 300);
      
      // Clean up function to clear the timeout
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page, searchTerm, selectedAssociation, selectedStatus, isAdmin, order, orderBy]);

  const fetchCustomers = async (associationId = null) => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page, // Use the selected rows per page value
        sort_by: orderBy,   // Field to sort by
        sort_order: order   // Sort direction (asc/desc)
      };
      
      // Backend has an issue when both search and status are provided
      // If we have both, we'll only send search to backend and filter status on client
      if (searchTerm) {
        params.search = searchTerm;
      } else if (selectedStatus) {
        params.status = selectedStatus;
      }
      
      // Add association_id parameter for superadmin
      if (isAdmin) {
        // Use provided associationId or the selectedAssociation from state
        params.association_id = associationId || selectedAssociation;
      }
      
      const response = await api.get('/api/customers', { params });
      
      // Apply client-side status filtering if needed
      let filteredCustomers = response.data.customers;
      if (searchTerm && selectedStatus) {
        filteredCustomers = response.data.customers.filter(customer => {
          // Handle different status formats
          const customerStatus = customer.status.toLowerCase();
          return customerStatus === selectedStatus.toLowerCase() ||
                 (selectedStatus === 'over_exposure' && 
                  (customerStatus === 'overexposure' || customerStatus === 'over exposure'));
        });
      }
      
      setCustomers(filteredCustomers);
      setPagination({
        total: searchTerm && selectedStatus ? filteredCustomers.length : response.data.total,
        pages: searchTerm && selectedStatus ? Math.ceil(filteredCustomers.length / pagination.per_page) : response.data.pages,
        current_page: response.data.current_page,
        per_page: pagination.per_page
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAssociationChange = (event) => {
    const newAssociationId = event.target.value;
    setSelectedAssociation(newAssociationId);
    
    // Note: We no longer need to set the allowedStatuses here
    // Since we're using the global context that manages them automatically
    const selectedAssoc = associations.find(assoc => assoc.id === newAssociationId);
      
    // Reset selected status if current selection is not allowed in new association
    if (selectedStatus && selectedAssoc && selectedAssoc.allowed_status) {
      // Convert API value back to display value for comparison
      let currentStatusDisplay;
      if (selectedStatus === 'over_exposure') {
        currentStatusDisplay = 'Over Exposure';
      } else if (selectedStatus) {
        currentStatusDisplay = selectedStatus
          .replace('_', ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      if (currentStatusDisplay && !selectedAssoc.allowed_status.includes(currentStatusDisplay)) {
        setSelectedStatus('');
      }
    }
    
    // Reset pagination when association changes
    setPagination({
      ...pagination,
      current_page: 1
    });
    
    // Immediately fetch customers with the new association
    fetchCustomers(newAssociationId);
  };

  const handleChangePage = (event, newPage) => {
    setPagination({
      ...pagination,
      current_page: newPage + 1
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({
      ...pagination,
      per_page: parseInt(event.target.value, 10),
      current_page: 1
    });
  };

  const handleViewCustomer = (id) => {
    navigate(`/customers/${id}`);
  };
  
  const handleOpenPaymentDialog = (customer) => {
    setSelectedCustomer(customer);
    setPaymentAmount('');
    setPaymentDialogOpen(true);
  };
  
  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedCustomer(null);
    setPaymentAmount('');
  };
  
  const handlePaymentAmountChange = (event) => {
    // Only allow positive numbers and decimals
    const value = event.target.value;
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setPaymentAmount(value);
    }
  };
  
  const handleSubmitPayment = async () => {
    if (!selectedCustomer || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    // Don't allow payment greater than outstanding amount
    if (parseFloat(paymentAmount) > selectedCustomer.your_outstanding) {
      toast.error('Payment amount cannot exceed outstanding amount');
      return;
    }
    
    try {
      setPaymentProcessing(true);
      
      const response = await api.post('/api/customers/payment-received', {
        customer_id: selectedCustomer.id,
        amount: parseFloat(paymentAmount)
      });
      
      toast.success('Payment recorded successfully');
      handleClosePaymentDialog();
      
      // Refresh the customer list to show updated outstanding amount
      fetchCustomers();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.error || 'Failed to record payment. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPagination({
      ...pagination,
      current_page: 1 // Reset to first page when search changes
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPagination({
      ...pagination,
      current_page: 1
    });
  };
  
  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
    // Reset pagination when status filter changes
    setPagination({
      ...pagination,
      current_page: 1
    });
  };
  
  const handleDownload = async () => {
    try {
      setLoading(true);
      // Prepare query parameters with current filters
      const params = {
        sort_by: orderBy,
        sort_order: order
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (selectedStatus) {
        params.status = selectedStatus;
      }
      
      if (isAdmin && selectedAssociation) {
        params.association_id = selectedAssociation;
      }
      
      // Make request with responseType blob to handle file download
      const response = await api.get('/api/customers/export', {
        params,
        responseType: 'blob'
      });
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers_export.xlsx');
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.success('Export successful');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download customers data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sorting when column header is clicked
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    // Reset to first page when sort changes
    setPagination({
      ...pagination,
      current_page: 1
    });
  };
  
  const formatStatus = (status) => {
    // Priority: use status_display from API if available
    if (typeof status === 'object' && status.status_display) {
      return status.status_display;
    }
    
    // If input is just the raw status string
    // Map status to standard display format
    let standardStatus;
    const statusLower = (typeof status === 'string') ? status.toLowerCase() : '';
    switch (statusLower) {
      case 'normal':
        standardStatus = 'Normal';
        break;
      case 'over_exposure':
      case 'overexposure':
      case 'over exposure':
        standardStatus = 'Over Exposure';
        break;
      case 'defaulter':
        standardStatus = 'Defaulter';
        break;
      case 'blacklisted':
        standardStatus = 'Blacklisted';
        break;
      case 'overdue':
        standardStatus = 'Overdue';
        break;
      default:
        standardStatus = status.charAt(0).toUpperCase() + status.slice(1);
    }
    
    // Don't map status to allowed list - preserve actual status value
    // Only format the display consistently
    return standardStatus;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Customers</Typography>
      </Box>
      
      {/* Association Dropdown for Admin */}
      {isAdmin && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="association-select-label">Select Association</InputLabel>
            <Select
              labelId="association-select-label"
              id="association-select"
              value={selectedAssociation}
              onChange={handleAssociationChange}
              label="Select Association"
            >
              {associations.map((association) => (
                <MenuItem key={association.id} value={association.id}>
                  {association.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}
      
      {/* Search and Status Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search customers"
              placeholder="Search by name, GSTIN or mobile number"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="status-select-label">Filter by Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={selectedStatus}
                onChange={handleStatusChange}
                label="Filter by Status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={loading}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {isAdmin ? (
                    <>
                      <SortableTableCell id="distributor_name" label="Distributor" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="name" label="Customer" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="gstin" label="GSTIN" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="mobile_no" label="Mobile No" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="status" label="Status" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="amount" label="Distributor Outstanding" align="right" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="percentage" label="% of Total" align="right" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                    </>
                  ) : (
                    <>
                      <SortableTableCell id="name" label="Name" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="gstin" label="GSTIN" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="mobile_no" label="Mobile No" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="status" label="Status" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="total_outstanding" label="Total Outstanding" align="right" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <SortableTableCell id="your_outstanding" label="Your Outstanding" align="right" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                      <TableCell>Actions</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 6} align="center">No customers found</TableCell>
                  </TableRow>
                ) : (
                  isAdmin ? (
                    // Admin view - flatten distributor outstandings
                    // First create a flat list of all rows for easier sorting
                    (() => {
                      // Create a flattened array for all customer-distributor pairs
                      const flattenedRows = customers.flatMap((customer) => {
                        if (customer.distributor_outstandings && customer.distributor_outstandings.length > 0) {
                          return customer.distributor_outstandings.map((dist) => ({
                            key: `${customer.id}-${dist.distributor_id}`,
                            distributor_name: dist.distributor_name,
                            customer_name: customer.name,
                            customer_gstin: customer.gstin,
                            customer_mobile_no: customer.mobile_no,
                            customer_status: customer.status,
                            amount: dist.amount,
                            percentage: dist.percentage
                          }));
                        } else {
                          return [{
                            key: `${customer.id}-empty`,
                            distributor_name: '-',
                            customer_name: customer.name,
                            customer_gstin: customer.gstin,
                            customer_mobile_no: customer.mobile_no,
                            customer_status: customer.status,
                            amount: 0,
                            percentage: 0
                          }];
                        }
                      });

                      // Return the JSX for rendering
                      return flattenedRows.map((row) => (
                        <TableRow key={row.key}>
                          <TableCell>{row.distributor_name}</TableCell>
                          <TableCell>{row.customer_name}</TableCell>
                          <TableCell>{row.customer_gstin || '-'}</TableCell>
                          <TableCell>{row.customer_mobile_no || '-'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={formatStatus(row.customer_status)} 
                              size="small" 
                              variant="filled"
                              sx={{
                                ...(row.customer_status.toLowerCase() === 'normal' && {
                                  bgcolor: 'success.main',
                                  color: 'success.contrastText',
                                  '& .MuiChip-label': {
                                    color: 'success.contrastText',
                                  }
                                }),
                                ...(['over exposure', 'over_exposure', 'overexposure'].includes(row.customer_status.toLowerCase()) && {
                                  bgcolor: 'info.main',
                                  color: 'info.contrastText',
                                  '& .MuiChip-label': {
                                    color: 'info.contrastText',
                                  }
                                }),
                                ...(row.customer_status.toLowerCase() === 'defaulter' && {
                                  bgcolor: 'warning.main',
                                  color: 'warning.contrastText',
                                  '& .MuiChip-label': {
                                    color: 'warning.contrastText',
                                  }
                                }),
                                ...(row.customer_status.toLowerCase() === 'blacklisted' && {
                                  bgcolor: 'error.main',
                                  color: 'error.contrastText',
                                  '& .MuiChip-label': {
                                    color: 'error.contrastText',
                                  }
                                })
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            ₹{row.amount.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {row.percentage > 0 ? `${row.percentage}%` : '0%'}
                          </TableCell>
                        </TableRow>
                      ));
                    })()
                  ) : (
                    // Regular user view
                    customers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell>{customer.gstin || '-'}</TableCell>
                        <TableCell>{customer.mobile_no || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={customer.status_display || formatStatus(customer.status)} 
                            size="small" 
                            variant="filled"
                            sx={{
                              ...((customer.status_display?.toLowerCase() === 'normal' || customer.status.toLowerCase() === 'normal') && {
                                bgcolor: 'success.main',
                                color: 'success.contrastText',
                                '& .MuiChip-label': {
                                  color: 'success.contrastText',
                                }
                              }),
                              ...((customer.status_display?.toLowerCase() === 'over exposure' || ['over exposure', 'over_exposure', 'overexposure'].includes(customer.status.toLowerCase())) && {
                                bgcolor: 'info.main',
                                color: 'info.contrastText',
                                '& .MuiChip-label': {
                                  color: 'info.contrastText',
                                }
                              }),
                              ...((customer.status_display?.toLowerCase() === 'defaulter' || customer.status.toLowerCase() === 'defaulter') && {
                                bgcolor: 'warning.main',
                                color: 'warning.contrastText',
                                '& .MuiChip-label': {
                                  color: 'warning.contrastText',
                                }
                              }),
                              ...((customer.status_display?.toLowerCase() === 'blacklisted' || customer.status.toLowerCase() === 'blacklisted') && {
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                '& .MuiChip-label': {
                                  color: 'error.contrastText',
                                }
                              }),
                              ...((customer.status_display?.toLowerCase() === 'overdue' || customer.status.toLowerCase() === 'overdue') && {
                                bgcolor: 'error.light',
                                color: 'error.contrastText',
                                '& .MuiChip-label': {
                                  color: 'error.contrastText',
                                }
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          ₹{customer.total_outstanding.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₹{customer.your_outstanding.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex' }}>
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleViewCustomer(customer.id)}
                              sx={{ mr: 1 }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="success" 
                              onClick={() => handleOpenPaymentDialog(customer)}
                              disabled={customer.your_outstanding <= 0}
                            >
                              <PaymentIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {searchTerm && selectedStatus ? (
            // Custom client-side pagination when both filters are active
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {customers.length} {customers.length === 1 ? 'result' : 'results'}
              </Typography>
            </Box>
          ) : (
            // Server-side pagination when not using client-side filtering
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 50]}
              component="div"
              count={pagination.total}
              rowsPerPage={pagination.per_page}
              page={pagination.current_page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      )}
      
      {/* Payment Received Dialog */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={handleClosePaymentDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Record Payment Received</DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Record a payment received from <strong>{selectedCustomer.name}</strong>.
                <br />
                Current outstanding: <strong>₹{selectedCustomer.your_outstanding.toLocaleString()}</strong>
              </DialogContentText>
              <TextField
                autoFocus
                label="Payment Amount (₹)"
                type="text"
                fullWidth
                variant="outlined"
                value={paymentAmount}
                onChange={handlePaymentAmountChange}
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '[0-9]*(\.[0-9]{0,2})?'
                }}
                error={paymentAmount !== '' && (
                  parseFloat(paymentAmount) <= 0 || 
                  parseFloat(paymentAmount) > selectedCustomer.your_outstanding
                )}
                helperText={
                  paymentAmount !== '' && parseFloat(paymentAmount) <= 0 
                    ? 'Amount must be greater than 0' 
                    : paymentAmount !== '' && parseFloat(paymentAmount) > selectedCustomer.your_outstanding
                      ? 'Amount cannot exceed outstanding balance'
                      : ''
                }
                sx={{ mt: 1 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog} disabled={paymentProcessing}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitPayment} 
            color="primary" 
            variant="contained"
            disabled={
              paymentProcessing || 
              !paymentAmount || 
              parseFloat(paymentAmount) <= 0 || 
              (selectedCustomer && parseFloat(paymentAmount) > selectedCustomer.your_outstanding)
            }
          >
            {paymentProcessing ? 'Processing...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomersList;